import { ICommand } from 'core-ts-types';
import { IPersonalReminderStateParams, ReminderQuery } from 'data-ng-personal-reminders';
import { INavigationState, NavigationHistoryStack } from 'ui-ng-navbar';
import { IPopupConfigService } from 'ui-ng-popup';
import { TypeUtility } from 'utility-ts-type';
import * as _4 from 'lodash4';

interface ITimeObject {
  name: string;
}

interface ISeperatedTimeObject {
  due_dt: string;
  due_tm: string;
}

interface IDesktopPersonalReminder {
  reminderTitle: string;
  isAllDayReminder: boolean;
  selectedTime: ITimeObject;
  reminderNotes: string;
  selectedDate: string;
  displayDate?: string;
  initialSelectedTime?: string;
}

export class NewPersonalReminderController {
  private timingArray: ITimeObject[];
  private dateArray: ITimeObject[];
  private personalReminder: IDesktopPersonalReminder;
  private maxTitleLength: number;
  private maxNotesLength: number;
  private isEditingMode: boolean;
  private YYYY_MM_DD: string = 'YYYY-MM-DD';
  private isOpened: boolean = true;
  private showValidationAlert: boolean = false;
  private validationMessage: string = 'Please fill the required fields.';

  constructor(
    private $state: angular.ui.IStateService,
    private createReminderCommand: ICommand,
    private editReminderCommand: ICommand,
    private ngReminderQuery: ReminderQuery,
    private phxNotificationDelegate: any,
    private $stateParams: IPersonalReminderStateParams,
    private ngTypeUtility: typeof TypeUtility,
    private deleteReminderCommand: ICommand,
    private ngNavigationHistoryStack: NavigationHistoryStack,
    private ngPopupService: IPopupConfigService
  ) {
    this.maxNotesLength = 1000;
    this.maxTitleLength = 60;
    this.personalReminder = {} as IDesktopPersonalReminder;

    this.isEditingMode = !_4.isUndefined(this.$stateParams.personalTodoID);
    this.deleteReminder = this.deleteReminder.bind(this);

    this.initializeTime();
  }

  $onInit(): void {
    this.isEditingMode ? this.setExistingReminderData() : this.initializeDefaultSettings();
  }

  closeModal(reload: boolean = false): void {
    const previousState: INavigationState = this.ngNavigationHistoryStack.previousState;
    this.$state.go(previousState.name, previousState.params, { reload });
  }

  showDialog() {
    this.isOpened = false;
  }

  sanitizeAndSaveReminderTitle(evt: ng.IAngularEvent): void {
    this.personalReminder.reminderTitle = this.sanitizeText(_4.get(evt, 'detail.value', ''), this.maxTitleLength);
  }

  sanitizeAndSaveReminderNotes(evt: ng.IAngularEvent): void {
    this.personalReminder.reminderNotes = this.sanitizeText(_4.get(evt, 'target.value', ''), this.maxNotesLength);
  }

  sanitizeText(text: string, length: number): string {
    if (text.length > length) {
      text = text.substring(0, length);
    }
    text = this.ngTypeUtility.string.sanitizeString(text);
    return text;
  }

  isReminderFormValid(): boolean {
    if (_4.isEmpty(this.personalReminder.reminderTitle)) {
      this.validationMessage = 'Please enter a reminder title for the reminder.';
      return false;
    }
    if (!this.isDateTimeCorrectlySet()) {
      this.validationMessage = 'Please select a valid due date for the reminder.';
      return false;
    }
    return true;
  }

  isDateTimeCorrectlySet(): boolean {
    if (this.personalReminder.isAllDayReminder) {
      return !_4.isEmpty(this.personalReminder.selectedDate);
    }
    return !_4.isEmpty(this.personalReminder.selectedDate) && !_4.isEmpty(this.personalReminder.selectedTime);
  }

  confirmReminderSubmission(): void {
    if (!this.isReminderFormValid()) {
      this.showValidationAlert = true;
      return;
    }
    if (this.isEditingMode) {
      this.showValidationAlert = false;
      this.editReminderCommand(this.$stateParams.personalTodoID, this.getReminderAPIValues())
        .then((response: any) => {
          this.submissionSuccess('updated');
        })
        .catch((error: any) => {
          this.notify('Something went wrong. Please try again!', true);
        });
    } else {
      this.showValidationAlert = false;
      this.createReminderCommand(this.getReminderAPIValues())
        .then((response: any) => {
          this.submissionSuccess('created');
        })
        .catch((error: any) => {
          this.notify('Something went wrong. Please try again!', true);
        });
    }
  }

  deleteReminder(): void {
    this.deleteReminderCommand(this.$stateParams.personalTodoID)
      .then((response: any) => {
        this.submissionSuccess('deleted');
      })
      .catch((error: any) => {
        this.notify(error, true);
      });
  }

  submissionSuccess(type: string): void {
    this.notify(`Personal to-do ${type} successfully!`, false);
    type === 'deleted' ? this.ngNavigationHistoryStack.deleteAndGoBack() : this.closeModal(true);
  }

  getReminderAPIValues(): object {
    return {
      title: this.personalReminder.reminderTitle,
      description: this.personalReminder.reminderNotes || '',
      due_dt: this.getDueDateTime().due_dt,
      due_tm: this.getDueDateTime().due_tm,
      status: 'ACTIVE'
    };
  }

  getDueDateTime(): ISeperatedTimeObject {
    const selectedDateTime = this.personalReminder.isAllDayReminder
      ? this.personalReminder.selectedDate
      : `${this.personalReminder.selectedDate} ${this.personalReminder.selectedTime.name}`;

    return {
      due_dt: moment(selectedDateTime, 'YYYY-MM-DD hh:mm A').format(this.YYYY_MM_DD),
      due_tm: moment(selectedDateTime, 'YYYY-MM-DD hh:mm A').format('HH:mm:ss.SSSS')
    };
  }

  private initializeTime(): any {
    const STARTTIME = '08:00';
    const INTERVAL = 15;
    const PERIOD = 'm';

    const periodsInADay = moment.duration(1, 'day').as(PERIOD) / INTERVAL;
    const startTime = moment(STARTTIME, 'hh:mm');

    this.timingArray = [];

    _4.times(periodsInADay, (key) => {
      startTime.add(key === 0 ? 0 : INTERVAL, PERIOD);
      this.timingArray.push({ name: startTime.format('hh:mm A') });
    });
  }

  notify(message: string, isError: boolean): void {
    const notificationDelegate = this.phxNotificationDelegate.restrictTo('app-alerts');
    if (isError) {
      notificationDelegate.error({
        content: message,
        showIcon: true,
        dismissButton: true,
        dismissOnTimeout: true
      });
    } else {
      notificationDelegate.success({
        content: message,
        showIcon: true,
        dismissButton: true,
        dismissOnTimeout: true
      });
    }
  }

  getDisplayDate(): string {
    return moment(this.personalReminder.selectedDate).format('MMM DD, YYYY');
  }

  setSelectedDate(evt: any): void {
    this.personalReminder.selectedDate = evt.detail.value;
    this.personalReminder.displayDate = this.getDisplayDate();
  }

  setSelectedTime(evt: any): void {
    this.personalReminder.selectedTime = {
      name: evt.detail.value
    };
  }

  setExistingReminderData(): void {
    const reminderData = this.ngReminderQuery.result;
    this.personalReminder.reminderTitle = reminderData.title;
    this.personalReminder.reminderNotes = reminderData.description;
    this.personalReminder.selectedDate = reminderData.due_dt;
    this.personalReminder.displayDate = this.getDisplayDate();

    const dateTime = moment(reminderData.due_tm as string, 'HH:mm:ss');
    this.personalReminder.isAllDayReminder = dateTime.hours() === 0 && dateTime.minutes() === 0;

    if (!this.personalReminder.isAllDayReminder) {
      this.personalReminder.selectedTime = { name: dateTime.format('hh:mm A') };
      this.personalReminder.initialSelectedTime = dateTime.format('HH:mm');
    }
  }

  initializeDefaultSettings(): void {
    this.personalReminder.isAllDayReminder = false;

    const currentDateTime: moment.Moment = moment();
    this.personalReminder.selectedDate = currentDateTime.format(this.YYYY_MM_DD);
    this.personalReminder.displayDate = this.getDisplayDate();
    const baseTime = currentDateTime.minutes(Math.ceil(currentDateTime.minute() / 15) * 15);
    this.personalReminder.selectedTime = {
      name: baseTime.format('hh:mm A')
    };
    this.personalReminder.initialSelectedTime = baseTime.format('HH:mm');
  }

  deleteConfirmationAlert(): void {
    const self = this;
    this.ngPopupService.show({
      title: 'Delete Personal To-Do',
      body: 'Are you sure you want to delete this personal to-do? This cannot be undone.',
      completeButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      completeCallback: self.deleteReminder,
      cancelCallback: angular.noop
    });
  }
}



// WEBPACK FOOTER //
// ./app/lib/create_personal_reminder/create_personal_reminder.ts